import { useEffect, useRef, useState } from 'react';
import { useLabels } from 'wsm-common-data';
import { useLocationsMakeFilter } from './useLocationsMakeFilter';
import { useLocationsRadius } from './useLocationsRadius';
import {
	prepareMakeFilters,
	prepareRadiusFilter
} from '../utils/prepareFilterItems';
import { FILTER_TYPES, DEFAULT_RANGE } from '../global-constants';

export const useActiveFilters = (isReady, isDefaultView = false) => {
	const labels = useLabels();
	const { makeFilters, removeMakeFilter, resetMakeFilters } =
		useLocationsMakeFilter(isReady);
	const { radius, updateRadius } = useLocationsRadius(isReady);

	// Track whether the user has interacted (radius, makes, or location change)
	// since mount. The default-radius chip should stay hidden on initial
	// render and appear only after a user-driven change while radius is
	// still the default.
	const skipFirstRun = useRef(true);
	const [hasUserInteracted, setHasUserInteracted] = useState(false);

	// Stable key so the effect doesn't fire on identity-only changes to the
	// makeFilters array reference.
	const makeFiltersKey = JSON.stringify(makeFilters);

	useEffect(() => {
		if (skipFirstRun.current) {
			skipFirstRun.current = false;
			return;
		}

		setHasUserInteracted(true);
	}, [radius, makeFiltersKey, isDefaultView]);

	const showDefaultRadius = hasUserInteracted && radius === DEFAULT_RANGE;

	const filters = [
		...prepareMakeFilters(makeFilters),
		prepareRadiusFilter(radius, labels, showDefaultRadius)
	].filter(Boolean);

	const filterRemoveHandlers = {
		[FILTER_TYPES.MAKE]: (id) => removeMakeFilter(id),
		[FILTER_TYPES.RADIUS]: () => updateRadius()
	};
	const handleFilterRemove = (type, id) => {
		if (!isReady) return;
		filterRemoveHandlers[type]?.(id);
	};

	const handleClearAllFilters = () => {
		if (!isReady) return;
		updateRadius();
		resetMakeFilters();
	};

	return { filters, handleFilterRemove, handleClearAllFilters };
};
